import React, { useState, useEffect } from 'react';
import { Plus, ChevronLeft, Check, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import FlowStep from './FlowStep';
import ActionPreview from './ActionPreview';

/**
 * OpportunityCreationFlow Component
 *
 * Manages the multi-step conversational flow for creating opportunities.
 * Steps:
 * 1. Select OEM (if not already specified)
 * 2. Select Program (from IHS data or custom)
 * 3. Select Segment
 * 4. Select Revenue Estimate
 * 5. Confirm and Create
 *
 * Props:
 * - flowData: Initial flow data from AI response
 * - onComplete: (opportunityData) => void - Called when flow completes successfully
 * - onCancel: () => void - Called when user cancels the flow
 * - onSendMessage: (message) => void - Called to send follow-up messages to AI
 * - isLoading: Boolean - External loading state
 */
const OpportunityCreationFlow = ({
  flowData,
  onComplete,
  onCancel,
  onSendMessage,
  isLoading = false
}) => {
  const [step, setStep] = useState(1);
  const [collectedData, setCollectedData] = useState({
    oem: flowData?.flow_state?.collected_data?.oem || '',
    program_name: '',
    program_id: null,
    segment: '',
    revenue_estimate: '',
    sop_date: null,
    platform_code: '',
    notes: flowData?.flow_state?.collected_data?.notes || ''
  });
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get available options from flow data
  const programs = flowData?.flow_options?.options || [];
  const segments = flowData?.segments || flowData?.flow_options?.segments || [];
  const oemOptions = flowData?.flow_options?.oem_options || [];

  // Calculate which step to start on
  useEffect(() => {
    if (flowData?.flow_state?.collected_data?.oem) {
      // OEM already specified, start at program selection
      if (programs.length > 0) {
        setStep(2); // Program selection
      } else {
        setStep(2); // Custom program entry
      }
    }
  }, [flowData]);

  const steps = [
    { id: 1, name: 'OEM', completed: !!collectedData.oem },
    { id: 2, name: 'Program', completed: !!collectedData.program_name },
    { id: 3, name: 'Segment', completed: !!collectedData.segment },
    { id: 4, name: 'Value', completed: !!collectedData.revenue_estimate },
    { id: 5, name: 'Confirm', completed: false }
  ];

  const handleOEMSelect = (oem) => {
    setCollectedData(prev => ({ ...prev, oem }));
    // Request programs for this OEM
    onSendMessage?.(`Show me ${oem} programs for a new opportunity`);
    setStep(2);
  };

  const handleProgramSelect = (programName, programData) => {
    setCollectedData(prev => ({
      ...prev,
      program_name: programName,
      program_id: programData?.id || null,
      platform_code: programData?.code || '',
      sop_date: programData?.sop_date || null
    }));
    setStep(3);
  };

  const handleCustomProgram = (customName) => {
    setCollectedData(prev => ({
      ...prev,
      program_name: customName,
      program_id: null
    }));
    setStep(3);
  };

  const handleSegmentSelect = (segment) => {
    setCollectedData(prev => ({ ...prev, segment }));
    setStep(4);
  };

  const handleRevenueSelect = (revenueEstimate) => {
    setCollectedData(prev => ({ ...prev, revenue_estimate: revenueEstimate }));
    setStep(5);
    // Build preview
    buildPreview({ ...collectedData, revenue_estimate: revenueEstimate });
  };

  const buildPreview = (data) => {
    const revenueLabels = {
      under_1m: '< $1M',
      '1m_5m': '$1M - $5M',
      '5m_10m': '$5M - $10M',
      '10m_plus': '$10M+',
      not_sure: 'Not sure yet'
    };

    setPreviewData({
      entity: {
        type: 'Opportunity',
        name: data.program_name,
        context: `${data.oem} • ${data.segment || 'No segment'}`
      },
      changes: [
        {
          field: 'Program Name',
          oldValue: null,
          newValue: data.program_name,
          highlight: true
        },
        {
          field: 'OEM',
          oldValue: null,
          newValue: data.oem
        },
        {
          field: 'Stage',
          oldValue: null,
          newValue: 'Target'
        },
        {
          field: 'Segment',
          oldValue: null,
          newValue: data.segment || 'Not specified'
        },
        {
          field: 'Revenue Estimate',
          oldValue: null,
          newValue: revenueLabels[data.revenue_estimate] || data.revenue_estimate
        },
        ...(data.sop_date ? [{
          field: 'SOP Date',
          oldValue: null,
          newValue: data.sop_date
        }] : [])
      ],
      confirmationRequired: true,
      reasonOptions: [
        'Customer RFQ or inquiry received',
        'Market intelligence / IHS data',
        'Strategic account target',
        'Proactive business pursuit'
      ]
    });
    setShowPreview(true);
  };

  const handleConfirm = async (reason) => {
    setLocalLoading(true);
    setError(null);

    try {
      // Build the create message for AI
      const createMessage = `Create opportunity with: program_name="${collectedData.program_name}", oem="${collectedData.oem}", segment="${collectedData.segment}", revenue_estimate="${collectedData.revenue_estimate}"${collectedData.program_id ? `, program_id="${collectedData.program_id}"` : ''}${collectedData.sop_date ? `, sop_date="${collectedData.sop_date}"` : ''}${reason ? `, notes="Reason: ${reason}"` : ''}`;

      // Send to AI for execution
      onSendMessage?.(createMessage);
      onComplete?.(collectedData);
    } catch (err) {
      setError(err.message || 'Failed to create opportunity');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleBack = () => {
    setShowPreview(false);
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-4 px-2">
      {steps.map((s, idx) => (
        <React.Fragment key={s.id}>
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors',
                s.completed
                  ? 'bg-[#07689f] text-white'
                  : s.id === step
                  ? 'bg-[#e8f4fa] text-[#07689f] border-2 border-[#07689f]'
                  : 'bg-gray-100 text-gray-400'
              )}
            >
              {s.completed ? <Check className="h-4 w-4" /> : s.id}
            </div>
            <span className={cn(
              'text-[10px] mt-1',
              s.id === step ? 'text-[#07689f] font-medium' : 'text-gray-400'
            )}>
              {s.name}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div className={cn(
              'flex-1 h-0.5 mx-1',
              steps[idx].completed ? 'bg-[#07689f]' : 'bg-gray-200'
            )} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderCurrentStep = () => {
    if (showPreview) {
      return (
        <ActionPreview
          actionData={previewData}
          onConfirm={handleConfirm}
          onCancel={() => setShowPreview(false)}
        />
      );
    }

    switch (step) {
      case 1:
        return (
          <FlowStep
            stepData={{
              step_name: 'select_oem',
              step_title: 'Select OEM',
              step_description: 'Which OEM is this opportunity for?',
              options_type: 'oem_selection',
              oem_options: oemOptions,
              allow_custom: true,
              custom_placeholder: 'Or type a custom OEM name...'
            }}
            onSelect={(value) => handleOEMSelect(value)}
            onCustomInput={(text) => handleOEMSelect(text)}
            isLoading={isLoading || localLoading}
          />
        );

      case 2:
        return (
          <FlowStep
            stepData={{
              step_name: 'select_program',
              step_title: `Select ${collectedData.oem} Program`,
              step_description: programs.length > 0
                ? `Found ${programs.length} program(s). Select one or enter a custom name.`
                : 'Enter the program/vehicle name',
              options_type: 'program_cards',
              options: programs,
              allow_custom: true,
              custom_placeholder: 'Or type a custom program name...'
            }}
            onSelect={(value, data) => handleProgramSelect(value, data)}
            onCustomInput={(text) => handleCustomProgram(text)}
            isLoading={isLoading || localLoading}
          />
        );

      case 3:
        return (
          <FlowStep
            stepData={{
              step_name: 'select_segment',
              step_title: 'Product Segment',
              step_description: 'What type of parts will this opportunity involve?',
              options_type: 'segment_buttons',
              segments: segments.length > 0 ? segments : undefined,
              allow_custom: true,
              custom_placeholder: 'Or type a custom segment...'
            }}
            onSelect={(value) => handleSegmentSelect(value)}
            onCustomInput={(text) => handleSegmentSelect(text)}
            isLoading={isLoading || localLoading}
          />
        );

      case 4:
        return (
          <FlowStep
            stepData={{
              step_name: 'select_revenue',
              step_title: 'Revenue Estimate',
              step_description: 'What is the estimated value of this opportunity?',
              options_type: 'revenue_buttons'
            }}
            onSelect={(value) => handleRevenueSelect(value)}
            isLoading={isLoading || localLoading}
          />
        );

      case 5:
        return (
          <div className="text-center py-4">
            <Loader2 className="h-6 w-6 text-[#07689f] animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Building preview...</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-[#e8f4fa] px-4 py-3 border-b border-[#b8dff5]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-[#07689f]" />
            <span className="font-medium text-[#07689f]">Create Opportunity</span>
          </div>
          {step > 1 && !showPreview && (
            <button
              onClick={handleBack}
              disabled={isLoading || localLoading}
              className="flex items-center gap-1 text-xs text-[#07689f] hover:underline"
            >
              <ChevronLeft className="h-3 w-3" />
              Back
            </button>
          )}
        </div>
      </div>

      {/* Step Indicator */}
      <div className="px-4 pt-4">
        {renderStepIndicator()}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Current Step Content */}
      <div className="p-4">
        {renderCurrentStep()}
      </div>

      {/* Cancel Button */}
      {!showPreview && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          <button
            onClick={onCancel}
            disabled={isLoading || localLoading}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default OpportunityCreationFlow;
